import { useCallback, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import APIService from 'api/APIService'
import { Settings, useSettings } from 'core/hooks/settings/useSettings'
import useLocalStorage from 'core/hooks/utils/useLocalStorage'
import { JobStatus } from 'types/misc'

export interface UseJobArgs {
  pollingJobKey: keyof Settings['pollingJobs']
  blockInterval?: boolean
  onProgress: (jobStatus: JobStatus) => void
  onSuccess: (jobStatus: JobStatus) => void
  onFailed: (jobStatus: JobStatus) => void
  onError: (jobId: string) => void
}

export const JOB_QUANTITY_REPLACEABLE_TAG = '[QUANTITY]'

const useJob = ({
  pollingJobKey,
  blockInterval = false,
  onProgress,
  onSuccess,
  onFailed,
  onError
}: UseJobArgs) => {
  const {
    data: { pollingJobs },
    set
  } = useSettings()
  const [localJobId, saveJobId] = useLocalStorage<string>(pollingJobKey, '')
  const jobID = pollingJobs[pollingJobKey].length
    ? pollingJobs[pollingJobKey]
    : localJobId

  const clearJobId = useCallback(() => {
    saveJobId('')
    set('pollingJobs', {
      ...pollingJobs,
      [pollingJobKey]: ''
    })
  }, [saveJobId, set, pollingJobs, pollingJobKey])

  const setJobId = useCallback(
    (jobId: string) => {
      saveJobId(jobId)
      set('pollingJobs', {
        ...pollingJobs,
        [pollingJobKey]: jobId
      })
    },
    [pollingJobKey, pollingJobs, saveJobId, set]
  )

  const { mutate: getJob } = useMutation({
    mutationFn: () => APIService.misc.getJobStatus({ jobId: jobID }),
    onSuccess(jobStatus) {
      if (jobStatus.status === 'in_progress') {
        onProgress?.(jobStatus)
        return
      }

      if (jobStatus.status === 'finished' && !jobStatus.data?.errors) {
        clearJobId()
        onSuccess(jobStatus)
        return
      }

      if (
        jobStatus.status === 'failed' ||
        (jobStatus.status === 'finished' && jobStatus.data?.errors)
      ) {
        clearJobId()
        onFailed(jobStatus)
        return
      }
    },
    onError(error) {
      console.error(error)
      onError(jobID)
      clearJobId()
    }
  })

  useEffect(() => {
    const interval = setInterval(() => {
      if (blockInterval) return
      if (jobID) {
        getJob()
      }
    }, 2000)
    return () => clearInterval(interval)
  }, [blockInterval, getJob, jobID])

  return {
    setJobId
  }
}

export default useJob
